/**
 * The running kiosk, as one object React components reach with `useApp()`: the session controller,
 * the UI store, the domain pack, the registries. It is built once at boot (src/app/boot.tsx) and
 * never changes identity — components subscribe to stores for what changes.
 */
import { createContext, use, type ReactNode } from 'react';
import type { Flags } from '../core/flags';
import type { Kiosk } from '../core/kiosk';
import type { Store } from '../core/store';
import type { UiState } from '../core/ui-state';
import type { Catalogs } from '../i18n/translator';
import type { Preset } from '../layout/model';
import type { KioskPack } from '../packs/contract';
import type { StageCatalog } from '../stage/catalog';
import type { WidgetDefinition } from '../widgets/contract';
import type { ModuleDefinition } from '../modules/contract';
import type { SceneDefinition } from '../shell/scenes';
import type { FaceRenderer } from '../avatar/face';
import type { FaceCue } from '../avatar/cues';
import type { VoiceController } from '../voice/controller';
import type { ReplayPlayer } from '../replay/player';
import type { ReviewContext } from '../review/gym-api';

export interface KioskApp {
  kiosk: Kiosk;
  ui: Store<UiState>;
  pack: KioskPack;
  /** The avatars and backgrounds this build has (src/app/stage.json). */
  stage: StageCatalog;
  presets: ReadonlyMap<string, Preset>;
  catalogs: Catalogs;
  modules: ReadonlyMap<string, ModuleDefinition>;
  widgets: ReadonlyMap<string, WidgetDefinition<never>>;
  scenes: ReadonlyMap<string, SceneDefinition>;
  flags: Flags;
  /** The voice pipeline, when this role has one (the kiosk; never an operator or a replay). */
  voice: VoiceController | null;
  /** The player, in a replay (and in a review, which replays the item's episode). */
  replay: ReplayPlayer | null;
  /** The gym review on screen (?review=): the run, the reviewer, the item. */
  review: ReviewContext | null;
  /** The faces drawn outside the platform (MuseTalk, three.js); null when there are none (?avatar=0, operator, replay). */
  avatar: FaceRenderer | null;
  /** The last emoji cue, for every face (avatar/cue-feed.ts). */
  cues: Store<FaceCue | null>;
}

const AppContext = createContext<KioskApp | null>(null);

export function AppProvider({ app, children }: { app: KioskApp; children: ReactNode }) {
  return <AppContext value={app}>{children}</AppContext>;
}

export function useApp(): KioskApp {
  const app = use(AppContext);
  if (!app) throw new Error('useApp() outside <AppProvider>');
  return app;
}
